<script setup>
import { computed } from 'vue'
import { rectConnection } from '../lib/motif-gallery.mjs'
const props = defineProps({ motif: Object })
const points = computed(() => {
  const count = Math.max(2, Math.min(6, Number(props.motif.nodes) || 2))
  const ranks = Array(count).fill(0)
  let changed = false
  for (let i = 0; i < count; i++) {
    changed = false
    for (const [a, b] of props.motif.edges) {
      if (a < 1 || b < 1 || a > count || b > count) continue
      if (ranks[b - 1] <= ranks[a - 1]) {
        ranks[b - 1] = ranks[a - 1] + 1
        changed = true
      }
    }
    if (!changed) break
  }
  if (changed)
    return ranks.map((_, i) => ({
      x: 50 + 34 * Math.cos(-Math.PI / 2 + (i * 2 * Math.PI) / count),
      y: 55 + 38 * Math.sin(-Math.PI / 2 + (i * 2 * Math.PI) / count),
    }))
  const levels = [...new Set(ranks)].sort((a, b) => a - b)
  return ranks.map((rank, i) => {
    const row = ranks.flatMap((value, index) => (value === rank ? [index] : []))
    return {
      x: row.length === 1 ? 50 : 16 + (row.indexOf(i) * 68) / (row.length - 1),
      y: levels.length === 1 ? 55 : 15 + (levels.indexOf(rank) * 80) / (levels.length - 1),
    }
  })
})
const validEdges = computed(() =>
  props.motif.edges.filter(([a, b]) => points.value[a - 1] && points.value[b - 1]),
)
const joint = computed(() => {
  const edges = validEdges.value
  if (props.motif.mode !== 'joint' || edges.length < 2 || !edges.every(([, b]) => b === edges[0][1]))
    return null
  const target = points.value[edges[0][1] - 1]
  const sources = edges.map(([a]) => points.value[a - 1])
  const junction = { x: target.x, y: target.y - 30, width: 0, height: 0 }
  return {
    branches: sources.map((source) => rectConnection({ ...source, width: 24, height: 24 }, junction, 0)),
    trunk: rectConnection(junction, { ...target, width: 24, height: 24 }, 1),
  }
})
const connections = computed(() =>
  validEdges.value.map(([a, b]) =>
    rectConnection(
      { ...points.value[a - 1], width: 24, height: 24 },
      { ...points.value[b - 1], width: 24, height: 24 },
      1,
    ),
  ),
)
</script>
<template>
  <svg viewBox="0 0 100 110" class="motif-preview" aria-hidden="true">
    <g v-if="joint" class="motif-joint" fill="none" stroke="currentColor" stroke-width="1.5">
      <path v-for="(branch, i) in joint.branches" :key="i" :d="branch.path" />
      <path :d="joint.trunk.path" />
      <polygon :points="joint.trunk.arrow" fill="currentColor" stroke="none" />
    </g>
    <g v-else v-for="(edge, i) in connections" :key="i" class="motif-arrow">
      <path :d="edge.path" fill="none" stroke="currentColor" stroke-width="1.5" />
      <polygon :points="edge.arrow" fill="currentColor" />
    </g>
    <g v-for="(p, i) in points" :key="i" class="motif-node">
      <rect
        :x="p.x - 12"
        :y="p.y - 12"
        width="24"
        height="24"
        rx="2"
        fill="#ffffff"
        stroke="currentColor"
        stroke-width="1.5"
      />
      <rect :x="p.x - 12" :y="p.y - 12" width="2" height="24" fill="currentColor" />
      <text :x="p.x" :y="p.y + 4" text-anchor="middle" font-size="12" font-weight="600" fill="currentColor">
        {{ String.fromCharCode(65 + i) }}
      </text>
    </g>
  </svg>
</template>
